import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button } from 'components/ui/button';
import { setConversationRejectedFromAssignment, setOwnerForConversation } from 'hooks/conversationListMutators';
import { useUpdateConversations } from 'hooks/useConversations';
import useSelectConversations from 'hooks/useSelectConversations';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import { toast } from 'sonner';
import { selectCurrentUser } from 'store/users/selectors';

export default function ConversationButtons() {
  // Hooks
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const updateConversations = useUpdateConversations();

  // Selectors
  const { conversationId, conversationMetadata } = useSelectConversations();

  const { loading: loadingSelfAssign, run: selfAssignConversation } = useRequest(
    () => ApiDoctorConversationsManager.selfAssignConversation({ conversationId }),
    {
      manual: true,
      onSuccess: () => {
        // Once the conversation has been transferred to the current caregiver, mark it
        // as part of 'My chats' optimistically (the websocket `conversation_assigned`
        // event will update its status to ASSIGNED_TO_CAREGIVER as well).
        if (currentUser) {
          updateConversations(setOwnerForConversation(conversationId, currentUser));
        }

        toast.success(t('caregivers.actions.selfAssign.success'));
      },
      onError: () => {
        toast.error(t('caregivers.actions.selfAssign.error'));
      },
    },
  );

  function selfAssign() {
    selfAssignConversation();
  }

  const { loading: loadingRejectAssignment, run: rejectAssignmentRequest } = useRequest(
    () => ApiDoctorConversationsManager.rejectAssignment({ conversationId }),
    {
      manual: true,
      onSuccess: () => {
        // Optimistically reflect the rejection immediately, without waiting for the
        // `conversation.assignment.rejected` websocket broadcast (which is what
        // actually brings it back into everyone's first-line waiting room). We
        // update the row in place rather than removing it — removing it here would
        // race with that websocket event potentially re-inserting the authoritative
        // row out of order and leaving the list transiently wrong; updating the
        // status in place instead converges to the same correct state regardless of
        // which arrives first, since the websocket handler's upsert is idempotent.
        updateConversations(setConversationRejectedFromAssignment(conversationId, 'FIRST_ASSIGNMENT_REJECTED'));

        toast.success(t('caregivers.actions.rejectAssignment.success'));
      },
      onError: () => {
        toast.error(t('caregivers.actions.rejectAssignment.error'));
      },
    },
  );

  function rejectAssignment() {
    rejectAssignmentRequest();
  }

  const isCurrentUserIsOwner =
    conversationMetadata &&
    conversationMetadata?.conversation &&
    conversationMetadata?.conversation.current_owner &&
    conversationMetadata.conversation.current_owner.id === currentUser?.id;

  // Show the self assign button if:
  // - there is no owner yet
  // - or there is an owner, but this is not the current user/caregiver
  const displaySelfAssign =
    !conversationMetadata?.conversation.current_owner ||
    !isCurrentUserIsOwner ||
    conversationMetadata?.type === 'unassigned';

  // Show "Refuser" only when this conversation has been specifically requested to
  // the current caregiver (ASSIGNMENT_TO_CAREGIVER_ASKED) and is still pending
  // their decision — i.e. they can accept it (self-assign) or push back.
  const displayRejectAssignment =
    conversationMetadata?.conversation.status.value === 'ASSIGNMENT_TO_CAREGIVER_ASKED' &&
    conversationMetadata?.conversation.status.registered_caregiver === currentUser?.id;

  if (!displaySelfAssign && !displayRejectAssignment) {
    return null;
  }

  return (
    <div className="flex w-full justify-center border-t border-border">
      <div className="flex items-center gap-2 p-3">
        {displayRejectAssignment && (
          <Button
            variant="destructive"
            size="lg"
            onClick={() => rejectAssignment()}
            disabled={loadingRejectAssignment}
            loading={loadingRejectAssignment}
          >
            <CloseOutlined />
            {t('chats.actions.rejectAssignment')}
          </Button>
        )}
        {displaySelfAssign && (
          <Button size="lg" onClick={() => selfAssign()} disabled={loadingSelfAssign} loading={loadingSelfAssign}>
            <CheckOutlined />
            {t('chats.actions.selfAssign')}
          </Button>
        )}
      </div>
    </div>
  );
}
